import { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import { AuthContext } from "../../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../../utils/constants/permissions";
import ContactPersonsTable from "./ContactPersonsTable";
import AddContactPersonForm from "./AddContactPersonForm";
import EditContactPersonForm from "./EditContactPersonForm";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { toast } from "../../../../../components/Toast/toast";
import { btn } from "../../../../../components/Button/buttonStyles";

// A contact is only reachable while the client itself is active, so the client's
// status gates every row rather than each contact standing on its own.
const isClientActive = (client) => client?.status === "Active";

const buildPrimaryUserRow = (client) => {
  if (!client) return null;
  const primary = client.primary_user || {};
  return {
    id: `primary-${client.id}`,
    isPrimary: true,
    first_name: primary.first_name || "",
    last_name: primary.last_name || "",
    email: client.auth?.email || "",
    gender: primary.gender || "",
    phone: primary.phone || "",
    teams_id: primary.teams_id || "",
    zoom_id: primary.zoom_id || "",
    position: "Primary User",
    // The primary user follows the client outright - there is no separate flag.
    is_active: isClientActive(client),
    is_invoicing: false,
  };
};

const ContactPersonsPage = ({ clientId, client }) => {
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.CLIENTS);
  const [contactPersons, setContactPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [personToDelete, setPersonToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  const fetchContacts = async (id) => {
    try {
      setLoading(true);
      const res = await api.get(`/client/contact-persons/${id}`, {
        withCredentials: true,
      });
      setContactPersons(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch contact persons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchContacts(clientId);
    }
  }, [clientId]);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) {
      next.set("action", nextAction);
    } else {
      next.delete("action");
    }
    if (id !== undefined && id !== null) {
      next.set("id", String(id));
    } else {
      next.delete("id");
    }
    setSearchParams(next, { replace: true });
  };

  const handleDeleteClick = (person) => {
    setPersonToDelete(person);
  };

  const handleDeleteConfirm = async () => {
    if (!personToDelete) return;
    try {
      await api.delete(`/client/contact-persons/${personToDelete.id}`, {
        withCredentials: true,
      });
      setPersonToDelete(null);
      fetchContacts(clientId);
    } catch (err) {
      console.error(err);
      setPersonToDelete(null);
      toast.error("Failed to delete contact person");
    }
  };

  const handleAdd = async (data) => {
    await api.post("/client/contact-persons", {
      ...data,
      client_id: clientId,
    });
    setAction(null);
    fetchContacts(clientId);
  };

  const handleUpdate = async (id, data) => {
    await api.put(`/client/contact-persons/${id}`, data, {
      withCredentials: true,
    });
    setAction(null);
    fetchContacts(clientId);
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return <div className="text-center mt-10 text-danger-600">{error}</div>;

  if (action === "add" && !readOnly) {
    return (
      <AddContactPersonForm
        onCancel={() => setAction(null)}
        onSave={handleAdd}
      />
    );
  }

  if (action === "edit" && editId && !readOnly) {
    const person = contactPersons.find((p) => String(p.id) === String(editId));
    if (!person) {
      return (
        <div className="text-center mt-10 text-gray-500">
          Contact person not found.
        </div>
      );
    }
    return (
      <EditContactPersonForm
        person={person}
        onCancel={() => setAction(null)}
        onSave={handleUpdate}
      />
    );
  }

  const clientActive = isClientActive(client);
  const primaryRow = buildPrimaryUserRow(client);
  // A contact of an inactive client reads as inactive whatever its own flag says.
  const contactRows = contactPersons.map((contact) => ({
    ...contact,
    is_active: clientActive && !!contact.is_active,
  }));
  const tableRows = primaryRow ? [primaryRow, ...contactRows] : contactRows;

  return (
    <div className="space-y-5">
      <section>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Contact Persons
          </h3>
          {!readOnly && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Contact Person
            </button>
          )}
        </div>

        <ContactPersonsTable
          persons={tableRows}
          readOnly={readOnly}
          onEdit={(person) => setAction("edit", person.id)}
          onDelete={handleDeleteClick}
        />
      </section>

      {personToDelete && (
        <ConfirmModal
          title="Delete Contact Person"
          message={`Are you sure you want to delete "${`${personToDelete.first_name || ""} ${personToDelete.last_name || ""}`.trim() || "this contact person"}"? This action cannot be undone.`}
          onCancel={() => setPersonToDelete(null)}
          onConfirm={handleDeleteConfirm}
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default ContactPersonsPage;
